import {Text} from '@dropbox/dig-components/typography';
import {atoms} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {WorkstreamsLine} from '@dropbox/dig-icons/assets';
import {NotificationCard} from 'components/notifications/NotificationCard';
import {GenericNotification} from 'components/notifications/types/GenericNotification';
import {useNotificationContext} from 'components/notifications/utils/useNotificationContext';
import {TimeAgo} from 'components/shared/TimeAgo';
import {isValid, parseISO} from 'date-fns';
import {t} from 'i18next';
import {Trans} from 'react-i18next';

type WorkstreamReadinessAttentionData = {
  count: number;
  as_of: string | null;
};

export const parseWorkstreamReadinessAttentionData = (
  rawData: string
): WorkstreamReadinessAttentionData | null => {
  try {
    const parsed: unknown = JSON.parse(rawData);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const {count, as_of: rawAsOf} = parsed as Record<string, unknown>;
    if (!Number.isInteger(count) || (count as number) < 1) {
      return null;
    }

    return {
      count: count as number,
      as_of: typeof rawAsOf === 'string' && isValid(parseISO(rawAsOf)) ? rawAsOf : null,
    };
  } catch {
    return null;
  }
};

export const WorkstreamReadinessAttention = () => {
  const {
    notification: {archived, data: notification},
  } = useNotificationContext();
  const data = parseWorkstreamReadinessAttentionData(notification.data);

  if (!data) {
    return <GenericNotification />;
  }

  return (
    <NotificationCard
      accessory={<UIIcon src={WorkstreamsLine} className={atoms({color: 'Text Subtle'})} />}
      header={
        <Trans
          i18nKey="workstream_readiness_attention"
          t={t}
          count={data.count}
          values={{count: data.count}}
          components={{b: <Text isBold />}}
        />
      }
      subtext={data.as_of ? <TimeAgo timestamp={data.as_of} /> : null}
      archived={archived}
      buttonLink={
        archived
          ? undefined
          : {
              to: '/workstreams/audit',
              cta: t('view'),
            }
      }
    />
  );
};
